import {
  blogpostReferenceValidator,
  pageReferenceValidator,
} from '@databases/types/confluence';
import { z } from 'zod';

import { serializePageReference, serializeSpaceReference } from './confluence/utils';
import { encodeCdata, encodeHtml } from './utils';

/**
 * A zod schema for the parameters of each Databases macro.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const macroParameterValidators = {
  jira: z.object({
    /** Comma-separated list of columns to show. {@see JiraColumn} */
    columns: z.string().optional(),
    /** JQL query (for multi issue macro). Either this or key must be specified. */
    jqlQuery: z.string().optional(),
    /** Issue key (for single issue macro). Either this or jqlQuery must be specified. */
    key: z.string().optional(),
    /** Jira instance display name */
    server: z.string().optional(),
    /** Jira instance server ID */
    serverId: z.string().optional(),
    /** Jira instance cloud ID */
    cloudId: z.string().optional(),
    /** Maximum number of issues to show */
    maximumIssues: z.number().optional(),
  }),
  'excerpt-include': z.object({
    '': pageReferenceValidator.or(blogpostReferenceValidator),
  }),
  test: z.object({
    // Used in unit tests
    param1: z.string().optional(),
  }),
};

export enum MacroBodyType {
  NONE = 'none',
  PLAIN_TEXT = 'plain-text',
  RICH_TEXT = 'rich-text',
}

/**
 * The body type of each macro. Must match the definition in atlassian-connect.json. The default is NONE.
 */
let macroBodyTypes = {
  jira: MacroBodyType.NONE,
  'excerpt-include': MacroBodyType.NONE,
  test: MacroBodyType.NONE, // Used in unit tests
};

// For unit tests
export function _setMacroBodyTypes(types: any): void {
  macroBodyTypes = types;
}

/**
 * The key of a macro.
 */
type MacroKey = keyof typeof macroParameterValidators;

/**
 * The parsed parameters of a macro.
 */
export type MacroParameters<Key extends MacroKey> = z.infer<
  (typeof macroParameterValidators)[Key]
>;

/**
 * Serialize macro parameters to XHTML storage format.
 */
export function serializeMacroParameters(
  parameters: Partial<MacroParameters<MacroKey>>
): string {
  return Object.entries(parameters)
    .flatMap(([key, value]) => {
      if (value == null) {
        return [];
      } else if (typeof value === 'string') {
        return [[key, encodeHtml(value)]];
      } else if (value.spaceKey) {
        return [[key, serializePageReference(value, true)]];
      } else if (value.key) {
        return [[key, serializeSpaceReference(value)]];
      } else {
        return [[key, encodeHtml(JSON.stringify(value))]];
      }
    })
    .map(
      ([key, html]) => `<ac:parameter ac:name="${encodeHtml(key)}">${html}</ac:parameter>`
    )
    .join('');
}

export enum MacroLayout {
  DEFAULT = 'default',
  WIDE = 'wide',
  FULL_WIDTH = 'full-width',
}

export interface MacroOptions {
  layout?: MacroLayout;
  macroId?: string;
}

export type MacroBody = { html: string } | string | undefined;

/**
 * Serialize a macro to XHTML storage format.
 */
export function serializeMacro<Key extends MacroKey>(
  macroKey: Key,
  parameters: MacroParameters<MacroKey>,
  body: MacroBody = undefined,
  options: MacroOptions = {}
): string {
  const idHtml = options.macroId ? ` ac:macro-id="${encodeHtml(options.macroId)}"` : '';
  const layoutHtml = options.layout ? ` data-layout="${encodeHtml(options.layout)}"` : '';
  const paramsHtml = serializeMacroParameters(parameters);
  const bodyElHtml = serializeMacroBody(macroKey, body);
  return `<ac:structured-macro ac:name="${encodeHtml(
    macroKey
  )}"${idHtml}${layoutHtml}>${paramsHtml}${bodyElHtml}</ac:structured-macro>`;
}

export function serializeMacroBody(
  macroKey: MacroKey,
  body: MacroBody = undefined
): string {
  if (macroBodyTypes[macroKey] === MacroBodyType.RICH_TEXT) {
    return typeof body === 'object'
      ? `<ac:rich-text-body>${body.html}</ac:rich-text-body>`
      : body
      ? `<ac:rich-text-body>${encodeHtml(body)}</ac:rich-text-body>`
      : '';
  } else if (macroBodyTypes[macroKey] === MacroBodyType.PLAIN_TEXT) {
    if (typeof body === 'object') {
      throw new Error('Cannot serialize rich text as a plain text body.');
    }
    return body ? `<ac:plain-text-body>${encodeCdata(body)}</ac:plain-text-body>` : '';
  } else if (body) {
    throw new Error('Cannot serialize a body for a bodyless macro.');
  } else {
    return '';
  }
}
